<template>
    <div id="receipt" class="receipt-wrapper">
        <div class="receipt">
            <h1>{{ companyName }}</h1>
            <hr class="receipt-hr">

            <div class="details">
                <div class="centered-label-number">
                    <div class="label">{{ $customTextFormatter.toSentenceCase(t('order')) }}</div>
                    <div class="big-number">{{ orderId }}</div>
                </div>
                <hr class="receipt-hr">
                <p><strong>{{ t('orderType') }}:</strong> {{ orderType }}</p>
                <p><strong>{{ t('tableNo') }}:</strong> {{ tableNo }}</p>
                <p><strong>{{ t('orderStatus') }}:</strong> {{ orderStatus }}
                </p>
                <p><strong>{{ t('paymentMethod') }}:</strong> {{ paymentMethod }}</p>
                <p><strong>{{ t('transaction') }}:</strong> {{ transactionStatus }}</p>
                <p><strong>{{ t('date') }}:</strong> {{ formattedDate }}</p>
                <hr class="receipt-hr">
            </div>

            <div class="items">
                <div v-for="item in orderItems" :key="item.id" class="item-entry">
                    <p class="item-name">
                        <strong>{{ item.food.name }} (x{{ item.quantity }})</strong>
                        <strong>{{ (item.price).toFixed(2) }}</strong>
                    </p>
                    <div class="item-details">
                        <div v-if="Object.keys(item.options).length" class="options">
                            <div class="options-label"><strong>{{ t('options') }}:</strong></div>
                            <div v-for="(opt, key) in item.options" :key="key" class="options-row">
                                <span class="options-key">{{ key }}: {{ opt.option }}</span>
                                <span class="options-value">{{ opt.value.toFixed(2) }}</span>
                            </div>
                        </div>
                        <div v-if="item.extras.length" class="extras">
                            <div class="extras-label"><strong>{{ t('extras') }}:</strong></div>
                            <div v-for="(extra, idx) in item.extras" :key="idx" class="extras-row">
                                <span class="extras-option">{{ extra.option }}</span>
                                <span class="extras-value">{{ extra.value.toFixed(2) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <hr class="receipt-hr">
            </div>

            <div class="totals">
                <p><strong>{{ t('subTotal') }}</strong> <strong>{{ subTotal.toFixed(2) }}</strong></p>
                <p v-if="getDiscount().value > 0">
                    <strong>{{ t('discount') }} ({{ getDiscount().discountType }})</strong>
                    <strong>-{{ getDiscount().value.toFixed(2) }}</strong>
                </p>
                <p><strong>{{ t('salesTax', { percent: taxRate * 100 }) }}</strong> <strong>{{ salesTax.toFixed(2)
                        }}</strong></p>
                <p><strong>{{ t('total') }}</strong> <strong>{{ totalWithTax.toFixed(2) }}</strong></p>
            </div>

            <div class="footer">
                <hr class="receipt-hr">
                <p>{{ t('thankYouForSupportingLocalBusiness') }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { getCurrentInstance, onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrderStore } from '@/stores/order';
import { useSystemSettingsStore } from '@/stores/systemSettings';
import { useOrder } from "@/composables/useOrder";

const { proxy } = getCurrentInstance(); 
const { locale, t } = useI18n();

const { getOrderItems, getTotalAmount, getOrderId, getOrderType, getOrderStatus, getPaymentMethod, getTransactionStatus, getDiscount, getCurrentTable } = useOrderStore();
const { getLanguage } = useSystemSettingsStore();
const { getOrderTypeString, getPaymentMethodString } = useOrder();

const currentDate = new Date();
const formattedDate = `${currentDate
    .getDate()
    .toString()
    .padStart(2, '0')}/${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${currentDate.getFullYear()} ${currentDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        })}`;

const companyName = process.env.VUE_APP_LOGO_TEXT;

const orderItems = ref(getOrderItems());
const orderId = ref(getOrderId());
const orderStatus = ref(proxy.$customTextFormatter.toSentenceCase(getOrderStatus()));
const orderType = ref(getOrderTypeString(getOrderType()));
const tableNo = ref(proxy.$customTextFormatter.formattedNumber(getCurrentTable()?.number))
const subTotal = ref(getTotalAmount());
const discount = ref(getDiscount());
const paymentMethod = ref(getPaymentMethodString(getPaymentMethod()));
const transactionStatus = ref(proxy.$customTextFormatter.toSentenceCase(getTransactionStatus()));
const systemLanguage = ref(getLanguage());

const taxRate = 0;
const salesTax = subTotal.value * taxRate;
const totalWithTax = subTotal.value - (discount.value.value || 0) + salesTax;

onMounted(() => {
  locale.value = 'en'; // To print out in english
});

onBeforeUnmount(() => {
  locale.value = systemLanguage.value.code; // Reset the locale language
});
</script>

<style scoped>
.receipt-wrapper {
    display: flex;
    justify-content: center;
    padding: 20px;
}

.receipt {
    width: 300px;
    padding: 20px;
    border: 1px solid #ccc;
    font-family: monospace;
    font-size: 14px;
    background-color: #f9f9f9;
    text-align: center;
}

.receipt h2 {
    margin: 0;
    font-size: 16px;
}

.receipt p {
    margin: 5px 0;
}

.details,
.date,
.items,
.totals,
.payment,
.footer {
    margin-top: 10px;
}

.details {
    text-align: left;
    margin-bottom: 20px;
}

.centered-label-number {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
}

.centered-label-number .label {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 1px;
    color: #333;
}

.centered-label-number .big-number {
    font-size: 40px;
    font-weight: bold;
    color: #111;
    margin-top: 2px;
    margin-bottom: 2px;
    line-height: 1.1;
}

.item-entry {
    margin-bottom: 10px;
}

.item-name {
    display: flex;
    justify-content: space-between;
}

.item-details {
    font-size: 12px;
    text-align: left;
}

.options,
.extras {
    color: #555;
}

.options-row,
.extras-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 15px;
}

.options-key,
.extras-option {
    flex: 1;
}

.options-value,
.extras-value {
    font-weight: bold;
    margin-left: 8px;
    min-width: 60px;
    text-align: right;
    display: inline-block;
}

.totals p {
    display: flex;
    justify-content: space-between;
}

.footer {
    margin-top: 20px;
    font-weight: bold;
}

.receipt-hr {
    margin: 20px 0;
    border-style: dashed;
}
</style>